import { useMemo } from 'react';

import {
  useCommitmentAssignmentsQuery,
  useCommitmentQuery,
} from 'hooks/queries/commitments';

import {
  normalizeAzureRiCommitment,
  normalizeAzureSpCommitment,
  normalizeAwsSpCommitment,
  normalizeAwsRiCommitment,
  normalizeGcpResourceCudCommitment,
  normalizeGcpFlexCudCommitment,
  normalizeAwsOdcrCommitment,
  normalizeAwsCapacityBlockCommitment,
  normalizeGcpReservationCommitment,
} from './_utils/normalizeCommitments';

type UseNormalizedCommitmentArgs<WithInstanceTypes extends boolean> = {
  commitmentId?: string;
  includeUsage?: boolean;
  withInstanceTypes?: WithInstanceTypes;
};

export const useNormalizedCommitment = <WithInstanceTypes extends boolean>({
  commitmentId,
  includeUsage = true,
  withInstanceTypes = false as WithInstanceTypes,
}: UseNormalizedCommitmentArgs<WithInstanceTypes>) => {
  const {
    data,
    isLoading: commitmentLoading,
    isError: isCommitmentError,
    error: commitmentError,
    refetch: refetchCommitments,
  } = useCommitmentQuery({
    commitmentId,
    includeUsage,
    withInstanceTypes,
  });
  const {
    data: assignments = [],
    isLoading: assignmentsLoading,
    isError: isAssignmentsError,
    error: assignmentsError,
    refetch: refetchAssignments,
  } = useCommitmentAssignmentsQuery({ commitmentId });

  const normalizedData = useMemo(() => {
    const commitment = data?.commitment;

    if (!commitment) {
      return undefined;
    }
    if (commitment.azureReservationContext) {
      return normalizeAzureRiCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.azureSavingsPlanContext) {
      return normalizeAzureSpCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.gcpResourceCudContext) {
      return normalizeGcpResourceCudCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.awsReservedInstancesContext) {
      return normalizeAwsRiCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.awsSavingsPlanContext) {
      return normalizeAwsSpCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.gcpFlexCudContext) {
      return normalizeGcpFlexCudCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.awsOdcrContext) {
      return normalizeAwsOdcrCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.awsCapacityBlockContext) {
      return normalizeAwsCapacityBlockCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    if (commitment.gcpCapacityReservationContext) {
      return normalizeGcpReservationCommitment<WithInstanceTypes>(
        commitment,
        assignments
      );
    }
    return undefined;
  }, [data, assignments]);

  return {
    normalizedData,
    isLoading: commitmentLoading || assignmentsLoading,
    isError: isCommitmentError || isAssignmentsError,
    refetch: () => {
      refetchCommitments();
      refetchAssignments();
    },
    error: commitmentError || assignmentsError,
  };
};
